import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { NauticusLogo } from "@/components/NauticusLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, ShieldCheck, Loader2, UserPlus, HeartHandshake, Lock, ThumbsUp, ThumbsDown, ChevronDown, Mic, MicOff, BarChart3 } from "lucide-react";
import { PlanComparePanel } from "@/components/PlanComparePanel";
import { DecisionCase } from "@/components/case/DecisionCase";
import { isPlanShopping } from "@/lib/planIntent";
import { useContactPrefill, ConsentOnFile } from "@/lib/useContactPrefill";
import { AskAboutCard } from "@/components/AskAboutCard";
import { TpmoDisclosure } from "@/components/sba/SbaShared";
import { fmtUsPhone } from "@/pages/sba/FounderFrontDoor";

// Pinned AEP starters in fixed order (no rotation). The first opens the supported 2027 comparison
// flow; the rest are general questions handled by the existing Ask handler.
const STARTERS = [
  { label: "Help me choose a 2027 plan", action: "finder" },
  { label: "What's changing in my plan for 2027?" },
  { label: "Will my doctors and prescriptions be covered?" },
  { label: "Are my Part B reduction or grocery benefits changing?" },
];

// Minimal inline renderer: renders **bold** safely and strips stray markdown asterisks so literal
// ** never shows in answers. No external markdown lib; newlines preserved by whitespace-pre-wrap.
function RichText({ text }) {
  const parts = String(text || "").split(/(\*\*[^*]+\*\*)/g);
  return (
    <p className="whitespace-pre-wrap">
      {parts.map((p, i) =>
        p.startsWith("**") && p.endsWith("**")
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : <React.Fragment key={i}>{p}</React.Fragment>
      )}
    </p>
  );
}

export default function AskNauticus() {
  const [token, setToken] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [suggestPersonalize, setSuggestPersonalize] = useState(false);
  const [hasHousehold, setHasHousehold] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [advisorOpen, setAdvisorOpen] = useState(false);
  const [advisorReason, setAdvisorReason] = useState(null);
  const [refAdvisor, setRefAdvisor] = useState(null);
  const attributed = (() => { try { const s = new URLSearchParams(window.location.search); return s.get("src") === "sba" || !!s.get("ref"); } catch { return false; } })();
  const [compareOpen, setCompareOpen] = useState(false);
  const [finderOpen, setFinderOpen] = useState(false);
  const [finderSeed, setFinderSeed] = useState(null);
  const [comparePrefill, setComparePrefill] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceErr, setVoiceErr] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const baseRef = useRef("");
  const listeningRef = useRef(false);
  const inputRefFocus = () => inputRef.current?.focus();
  const navigate = useNavigate();

  const voiceSupported = typeof window !== "undefined" && !!(window.SpeechRecognition || window.webkitSpeechRecognition);
  const voiceEnabled = voiceSupported;

  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const ref = sp.get("ref");
    try {
      if (ref) sessionStorage.setItem("founder_ref", ref); else sessionStorage.removeItem("founder_ref");
      if (sp.get("src") || ref) sessionStorage.setItem("ask_src", sp.get("src") || "sba"); else sessionStorage.removeItem("ask_src");
    } catch { /* ignore */ }
    // Advisor identity comes ONLY from this URL's ref (never stale storage) via the public front-door lookup.
    if (ref) {
      api.get(`/f/${encodeURIComponent(ref)}`).then((r) => setRefAdvisor(r.data)).catch(() => setRefAdvisor(null));
    } else {
      // Agency journey (src=sba) and direct unassigned /ask both show the SBA main line (Text only when
      // server-verified ready). Display only: never assigns households or changes attribution.
      api.get("/f/_agency/sba").then((r) => r.data?.business_phone && setRefAdvisor({ agency: true, business_display: "Senior Benefit Advisors", ...r.data })).catch(() => setRefAdvisor(null));
    }
    if (sp.get("help") === "advisor") setAdvisorOpen(true);
    const fwd = new URLSearchParams();
    ["ref", "src", "utm_source", "utm_medium", "utm_campaign", "utm_content", "v", "qa"].forEach((k) => { if (sp.get(k)) fwd.set(k, sp.get(k)); });
    const qs = fwd.toString() ? `?${fwd.toString()}` : "";
    let cancelled = false;
    const start = (attempt) => api.post(`/public/ask/session${qs}`).then((r) => {
      if (cancelled) return;
      setToken(r.data.session_token);
      try { localStorage.setItem("ask_session_token", r.data.session_token); } catch { /* ignore */ }
    }).catch(() => {
      if (cancelled) return;
      if (attempt < 2) setTimeout(() => start(attempt + 1), 4000 * (attempt + 1));
      else toast.error("We couldn't start your conversation. Please refresh the page in a moment.");
    });
    start(0);
    return () => { cancelled = true; };
  }, []);
  useEffect(() => {
    if (token) api.post(`/public/ask/${token}/event`, { event: "ask_page_opened" }).catch(() => {});
  }, [token]);
  // Follow the conversation only after real message activity; a fresh empty visit stays at the top.
  useEffect(() => {
    if (!messages.length && !sending) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);
  // Stop any active dictation when leaving the page.
  useEffect(() => () => { try { recognitionRef.current?.abort(); } catch { /* ignore */ } }, []);

  const push = (m) => setMessages((prev) => [...prev, m]);

  // --- Voice dictation (browser Web Speech API; no server, no stored audio) ---
  const toggleDictation = () => {
    if (!voiceSupported) {
      setVoiceErr("Voice typing isn't supported in this browser. Try Chrome, Edge, or Safari — or just type your question.");
      return;
    }
    if (listening) { listeningRef.current = false; try { recognitionRef.current?.stop(); } catch { /* ignore */ } return; }
    setVoiceErr(null);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = false;
    baseRef.current = input ? input.trim() + " " : "";
    rec.onresult = (e) => {
      if (!listeningRef.current) return; // ignore late events after Send/stop
      let finalText = "", interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalText += t; else interim += t;
      }
      setInput((baseRef.current + finalText + interim).replace(/\s+/g, " ").replace(/^\s/, ""));
      if (finalText) baseRef.current = (baseRef.current + finalText).replace(/\s+/g, " ");
    };
    rec.onerror = (e) => {
      listeningRef.current = false; setListening(false);
      if (e.error === "not-allowed" || e.error === "service-not-allowed")
        setVoiceErr("Microphone access was blocked. Allow mic access in your browser, or type your question instead.");
      else if (e.error === "no-speech")
        setVoiceErr("I didn't catch that. Tap the mic to try again, or type your question.");
      else if (e.error === "audio-capture")
        setVoiceErr("No microphone was found. You can type your question instead.");
      else setVoiceErr("Voice typing stopped. You can type your question.");
    };
    rec.onend = () => { setListening(false); listeningRef.current = false; };
    recognitionRef.current = rec;
    try { rec.start(); listeningRef.current = true; setListening(true); }
    catch { listeningRef.current = false; setListening(false); setVoiceErr("Couldn't start voice typing. You can type your question."); }
  };

  const sendFeedback = async (message_id, value) => {
    if (!token || !message_id) return;
    try {
      await api.post(`/public/ask/${token}/feedback`, { message_id, value });
      setMessages((prev) => prev.map((m) => (m.message_id === message_id ? { ...m, feedback: value } : m)));
    } catch { /* silent */ }
  };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || !token || sending) return;
    if (isPlanShopping(content)) {
      listeningRef.current = false; setListening(false);
      try { recognitionRef.current?.abort(); } catch { /* ignore */ }
      setInput(""); setFinderSeed(content); setFinderOpen(true);
      return;
    }
    // End dictation before clearing input so late recognition events can't repopulate it.
    listeningRef.current = false; setListening(false);
    try { recognitionRef.current?.abort(); } catch { /* ignore */ }
    setInput("");
    push({ role: "user", content });
    setSending(true);
    try {
      const { data } = await api.post(`/public/ask/${token}/message`, { content });
      push({ role: "assistant", content: data.reply, sources: data.sources || [], blocked: data.blocked,
             message_id: data.message_id, next_actions: data.next_actions || [], compare_prefill: data.compare_prefill || null });
      setRemaining(data.remaining);
      setSuggestPersonalize(!!data.suggest_personalize);
    } catch (e) {
      push({ role: "assistant", content: "Sorry — something went wrong. Please try again.", sources: [] });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F6F2] text-[#0B192C] flex flex-col" data-testid="ask-nauticus-page">
      {/* Header */}
      <header className="flex items-center justify-between px-5 sm:px-8 py-4 border-b border-white/10 bg-[#0B192C]">
        <Link to="/" aria-label="Nauticus home"><NauticusLogo light /></Link>
        <Link to="/login"><Button variant="ghost" size="sm" className="text-white/70 hover:text-white hover:bg-white/10" data-testid="ask-signin">Sign in</Button></Link>
      </header>
      {refAdvisor && (
        <div className="bg-[#FFFBEB] border-b border-[#E7D9A8]" data-testid="ask-ref-advisor">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-base text-[#0B192C]">
            <span>{refAdvisor.agency ? <>Licensed insurance agency: <strong data-testid="ask-ref-advisor-name">Senior Benefit Advisors</strong></> : <>Your licensed insurance advisor: <strong data-testid="ask-ref-advisor-name">{refAdvisor.business_display}</strong> · Senior Benefit Advisors</>}</span>
            {refAdvisor.business_phone && (
              <a href={`tel:${refAdvisor.business_phone}`} className="font-semibold underline" data-testid="ask-ref-advisor-phone">Call <span className="select-all">{fmtUsPhone(refAdvisor.business_phone)}</span></a>
            )}
            {refAdvisor.business_phone && refAdvisor.sms_ready && (
              <a href={`sms:${refAdvisor.business_phone}`} className="font-semibold underline" data-testid="ask-ref-advisor-text">Text {refAdvisor.agency ? "SBA" : (refAdvisor.business_display || "").split(" ")[0]}</a>
            )}
          </div>
        </div>
      )}

      <div className="flex-1 w-full max-w-3xl mx-auto px-4 sm:px-6 flex flex-col">
        {/* Intro */}
        {messages.length === 0 && (
          <div className="pt-10 pb-6" data-testid="ask-intro">
            <p className="text-[#1E3E62] text-sm font-semibold tracking-wide uppercase">Questions about Medicare?</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-tight text-[#0B192C]">Ask Nauticus.</h1>
            <p className="text-[#5B6670] mt-4 text-base max-w-xl">
              Calm, neutral Medicare guidance — plus side-by-side 2027 plan facts for your county. No
              account or phone number needed to start.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-2">
              {STARTERS.map((s) => (
                <button key={s.label} onClick={() => s.action === "compare" ? setCompareOpen(true) : s.action === "finder" ? setFinderOpen(true) : send(s.label)} data-testid="ask-suggestion"
                  className={`text-left text-base rounded-xl px-4 py-4 transition-colors shadow-sm border ${s.action ? "bg-[#0B192C] text-white border-[#0B192C] hover:bg-[#1E3E62] font-semibold" : "bg-white text-[#0B192C] hover:bg-[#0B192C]/5 border-[#E7E4DB] hover:border-[#1E3E62]/40"}`}>
                  {s.action ? <span className="inline-flex items-center gap-1.5"><BarChart3 className="w-4 h-4" /> {s.label}</span> : s.label}
                </button>
              ))}
            </div>
            <p className="text-[#8A8F98] text-xs mt-5 flex items-center gap-1.5">
              <Lock className="w-3 h-3" /> No account or phone number required. We save your
              details to My Nauticus only when you ask.{" "}
              <Link to="/privacy" className="underline hover:text-[#0B192C]" data-testid="ask-intro-privacy-link">How we protect your information</Link>
            </p>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 space-y-4 py-6" data-testid="ask-messages">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`ask-msg-${m.role}`}>
              <div className={`max-w-[90%] rounded-2xl px-4 py-3.5 text-base leading-relaxed ${m.role === "user" ? "bg-[#1E3E62]/10 border border-[#1E3E62]/20 text-[#0B192C] font-medium shadow-sm" : "bg-white border border-[#E7E4DB] text-[#0B192C] shadow-sm"}`}>
                <RichText text={m.content} />
                {m.role === "assistant" && (m.sources?.length > 0) && (
                  <details className="mt-3 pt-2 border-t border-[#E7E4DB]" data-testid="ask-sources">
                    <summary className="cursor-pointer text-[11px] text-[#5B6670] hover:text-[#0B192C] flex items-center gap-1 list-none" data-testid="ask-sources-toggle">
                      <ChevronDown className="w-3 h-3 shrink-0" /> What Nauticus relied on
                    </summary>
                    <p className="mt-2 mb-1 text-[10px] uppercase tracking-wide text-[#8A8F98]">Authoritative sources Nauticus used</p>
                    <div className="mt-1 flex flex-col gap-1" data-testid="ask-sources-list">
                      {m.sources.map((s) => (
                        <div key={s.key} data-testid="ask-source-ref" data-source-url={s.url}
                          className="flex items-start gap-1.5 text-[11px] text-[#5B6670]">
                          <ShieldCheck className="w-3 h-3 shrink-0 mt-0.5 text-[#1E3E62]" />
                          <span>
                            {s.label}
                            {(s.status || s.published || s.checked) && (
                              <span className="block text-[10px] text-[#8A8F98]" data-testid="ask-source-dates">
                                {[s.status, s.published || s.checked].filter(Boolean).join(" · ")}
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
                {m.role === "assistant" && (m.next_actions?.length > 0) && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="ask-deflection-actions">
                    {m.next_actions.includes("open_compare") && (
                      <button onClick={() => { setFinderOpen(true); }} className="text-[11px] bg-[#0B192C] text-white hover:bg-[#1E3E62] rounded-full px-4 py-2 text-sm font-semibold" data-testid="deflect-open-compare">Help me choose a 2027 plan</button>
                    )}
                    {m.next_actions.includes("save_review") && (
                      <button onClick={() => setSaveOpen(true)} className="text-[11px] bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620] rounded-full px-4 py-2 text-sm font-semibold" data-testid="cta-save-review">Save &amp; continue my review</button>
                    )}
                    {m.next_actions.includes("save_household") && (
                      <button onClick={() => setSaveOpen(true)} className="text-[11px] bg-[#0B192C]/5 hover:bg-[#0B192C]/10 text-[#0B192C] rounded-full px-4 py-2 text-sm" data-testid="deflect-save">Save my info to personalize</button>
                    )}
                    {m.next_actions.includes("add_current_plan") && (
                      <button onClick={() => setSaveOpen(true)} className="text-[11px] bg-[#0B192C]/5 hover:bg-[#0B192C]/10 text-[#0B192C] rounded-full px-4 py-2 text-sm" data-testid="deflect-add-plan">Add my current plan</button>
                    )}
                    {m.next_actions.includes("ask_general") && (
                      <button onClick={() => inputRefFocus()} className="text-[11px] bg-[#0B192C]/5 hover:bg-[#0B192C]/10 text-[#0B192C] rounded-full px-4 py-2 text-sm" data-testid="deflect-ask-general">Ask a general question</button>
                    )}
                    {m.next_actions.includes("request_advisor") && (
                      <button onClick={() => setAdvisorOpen(true)} className="text-[11px] bg-[#0B192C]/5 hover:bg-[#0B192C]/10 text-[#0B192C] rounded-full px-4 py-2 text-sm" data-testid="deflect-advisor">Get licensed insurance agent help</button>
                    )}
                    {m.next_actions.includes("keep_anonymous") && (
                      <button onClick={() => inputRefFocus()} className="text-[11px] bg-[#0B192C]/5 hover:bg-[#0B192C]/10 text-[#0B192C] rounded-full px-4 py-2 text-sm" data-testid="cta-keep-anonymous">Keep asking anonymously</button>
                    )}
                  </div>
                )}
                {m.role === "assistant" && m.message_id && (
                  <div className="mt-2 flex items-center gap-2" data-testid="ask-feedback">
                    <button onClick={() => sendFeedback(m.message_id, "helpful")} disabled={!!m.feedback}
                      className={`p-1 rounded ${m.feedback === "helpful" ? "text-[#1E3E62]" : "text-[#8A8F98] hover:text-[#0B192C]"}`} data-testid={`feedback-helpful-${m.message_id}`} aria-label="Helpful">
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => sendFeedback(m.message_id, "not_helpful")} disabled={!!m.feedback}
                      className={`p-1 rounded ${m.feedback === "not_helpful" ? "text-[#1E3E62]" : "text-[#8A8F98] hover:text-[#0B192C]"}`} data-testid={`feedback-not-helpful-${m.message_id}`} aria-label="Not helpful">
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                    {m.feedback && <span className="text-[10px] text-[#8A8F98]">Thanks for the feedback</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
          {sending && <div className="flex justify-start"><div className="bg-white border border-[#E7E4DB] rounded-2xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-[#1E3E62]" /></div></div>}
          <div ref={endRef} />
        </div>

        {/* Personalize / advisor prompts */}
        {(suggestPersonalize || hasHousehold) && (
          <div className="mb-3 flex flex-wrap gap-2" data-testid="ask-actions">
            {!hasHousehold && (
              <Button onClick={() => setSaveOpen(true)} className="bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620] rounded-full" data-testid="ask-save-open">
                <UserPlus className="w-4 h-4 mr-1.5" /> Save & personalize
              </Button>
            )}
            <Button onClick={() => setAdvisorOpen(true)} variant="outline" className="rounded-full border-[#1E3E62] text-[#1E3E62] hover:bg-[#1E3E62]/10" data-testid="ask-advisor-open">
              <HeartHandshake className="w-4 h-4 mr-1.5" /> Talk to a licensed insurance agent
            </Button>
          </div>
        )}
        {remaining !== null && remaining <= 2 && !hasHousehold && (
          <p className="text-[#5B6670] text-xs mb-2" data-testid="ask-remaining">{remaining} question{remaining === 1 ? "" : "s"} remaining before you need to create an account. Save your conversation to keep going and personalize.</p>
        )}
        {remaining === 0 && !hasHousehold && (
          <div className="mb-3 rounded-xl border border-[#1E3E62]/30 bg-[#1E3E62]/5 p-4" data-testid="ask-create-account">
            <p className="text-sm text-[#0B192C] font-semibold">Create an account to keep asking questions and continue with Nauticus.</p>
            <Button onClick={() => { try { localStorage.setItem("ask_save_intent", token); } catch { /* ignore */ } navigate("/register"); }} className="mt-2 bg-[#D4AF37] text-[#0B192C] hover:bg-[#B89620] rounded-full" data-testid="ask-create-account-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> Create your account
            </Button>
          </div>
        )}

        {/* Composer */}
        <div className="sticky bottom-0 bg-[#F7F6F2] pb-5 pt-2">
          {messages.length > 0 && <button type="button" onClick={() => setFinderOpen(true)} disabled={!token}
            className="mb-2 inline-flex min-h-9 items-center gap-1.5 rounded-full border border-[#0B192C] bg-white px-3 text-sm font-semibold text-[#0B192C]" data-testid="ask-open-finder">
            <BarChart3 className="w-4 h-4" /> My 2027 plan search
          </button>}
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <div className="flex-1 relative">
              <Input value={input} ref={inputRef} onChange={(e) => setInput(e.target.value)} placeholder="Ask a Medicare question, or tap Compare 2027 plans…"
                className="bg-white border-[#D7D4CB] text-[#0B192C] placeholder:text-[#8A8F98] h-12 rounded-full pl-5 pr-12 shadow-sm" data-testid="ask-input" disabled={!token} />
              {voiceEnabled && (
              <button type="button" onClick={toggleDictation} disabled={!token}
                aria-label={listening ? "Stop voice typing" : "Start voice typing"} data-testid="ask-mic"
                className={`absolute right-1.5 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${listening ? "bg-[#1E3E62] text-white animate-pulse" : "text-[#5B6670] hover:bg-[#0B192C]/5"}`}>
                {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </button>
              )}
            </div>
            <Button type="submit" disabled={!token || sending || !input.trim()}
              className={`rounded-full h-12 w-12 p-0 shadow-sm transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#1E3E62] ${(!token || sending || !input.trim()) ? "bg-[#E7E4DB] text-[#AFABA0] cursor-not-allowed hover:bg-[#E7E4DB]" : "bg-[#D4AF37] text-[#0B192C] hover:bg-[#C8A233] active:bg-[#B8941F]"}`}
              data-testid="ask-send">
              <Send className="w-5 h-5" />
            </Button>
          </form>
          {listening && (
            <p className="text-[#1E3E62] text-[11px] mt-2 text-center flex items-center justify-center gap-1.5" data-testid="ask-listening">
              <span className="w-2 h-2 rounded-full bg-[#1E3E62] animate-pulse" /> Listening… speak your question, then review it before sending.
            </p>
          )}
          {voiceErr && <p className="text-[#B45309] text-[11px] mt-2 text-center" data-testid="ask-voice-error">{voiceErr}</p>}
          {voiceEnabled && (
            <p className="text-[#8A8F98] text-[10px] mt-1.5 text-center" data-testid="ask-voice-notice">
              Voice typing uses your browser's speech service, which may process audio externally. You can always type instead.
            </p>
          )}
          <p className="text-[#8A8F98] text-[11px] mt-2 text-center">Nauticus shares general Medicare information and supported 2027 plan-fact comparisons for your county. Provider networks, prescription drug coverage, and personalized costs aren't covered here. Please don't type Social Security or Medicare (MBI) numbers.</p>
        </div>

        {/* AI / privacy disclosure */}
        <div className="pb-4 -mt-2" data-testid="ask-disclosure-wrap">
          <AskAboutCard testid="ask-disclosure" />
          {attributed && <div className="mt-3"><TpmoDisclosure testid="ask-tpmo" /></div>}
          <p className="mt-4 pb-6 text-center text-[11px] text-[#8A8F98]" data-testid="ask-operator-attribution">
            Nauticus is a platform operated by Navillus Technologies LLC.{" "}
            <Link to="/privacy" className="underline hover:text-[#0B192C]" data-testid="ask-footer-privacy-link">Privacy &amp; Security</Link>
          </p>
        </div>
      </div>

      <SaveDialog open={saveOpen} onOpenChange={setSaveOpen} token={token}
        onSaved={() => { setSaveOpen(false); try { localStorage.setItem("ask_save_intent", token); } catch { /* ignore */ } toast.success("Saved. Create your account to keep this conversation."); navigate("/register"); }} />
      <AdvisorDialog open={advisorOpen} onOpenChange={(o) => { setAdvisorOpen(o); if (!o) setAdvisorReason(null); }} token={token} hasHousehold={hasHousehold} reason={advisorReason}
        onNeedSave={() => { setAdvisorOpen(false); setSaveOpen(true); }} />
      <Dialog open={finderOpen} onOpenChange={(o) => { setFinderOpen(o); if (!o) setFinderSeed(null); }}>
        <DialogContent mobileFull className="max-w-5xl w-full sm:w-full h-[100dvh] sm:h-auto max-h-[100dvh] sm:max-h-[90vh] rounded-none sm:rounded-lg overflow-y-auto overflow-x-hidden p-4 sm:p-6 bg-[#F7F6F2]" data-testid="ask-finder-dialog">
          <DialogHeader><DialogTitle>Find your best-fit 2027 plans</DialogTitle>
            <DialogDescription className="text-[#5B6670]">Tell us your ZIP, doctors, medicines, health conditions, whether you have Medicaid, and what matters most. We'll ask only what we need, then show up to three explained choices side by side.</DialogDescription>
          </DialogHeader>
          {token && <DecisionCase base={`/public/ask/${token}/case`} variant="consumer" seed={finderSeed}
            contact={refAdvisor?.business_phone ? { phone: refAdvisor.business_phone, smsReady: !!refAdvisor.sms_ready, label: refAdvisor.agency ? "SBA" : (refAdvisor.business_display || "your advisor").split(" ")[0] } : null}
            onOpenLegacy={() => { setFinderOpen(false); setCompareOpen(true); }}
            onRequestAdvisor={() => { setFinderOpen(false); setAdvisorReason("Doctor and prescription review"); setAdvisorOpen(true); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-2xl w-[calc(100vw-1rem)] sm:w-full max-h-[88vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6" data-testid="ask-compare-dialog">
          <DialogHeader><DialogTitle>Compare 2027 Medicare plans</DialogTitle>
            <DialogDescription className="text-[#5B6670]">Pick your county, find plans by carrier, and see supported 2027 plan facts side by side — no sign-in needed. Add your doctors and medications below the plans to check them plan by plan.</DialogDescription>
          </DialogHeader>
          <PlanComparePanel key={comparePrefill?.carrier || "blank"} variant="public" token={token} prefillCarrier={comparePrefill?.carrier || ""} onRequestAdvisor={(reason) => { setCompareOpen(false); setAdvisorReason(typeof reason === "string" ? reason : null); setAdvisorOpen(true); }} onSaveInvite={() => { setCompareOpen(false); setSaveOpen(true); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SaveDialog({ open, onOpenChange, token, onSaved }) {
  const [form, setForm] = useState({ needs: [] });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    if (!open || !token) return;
    setLoading(true);
    api.post(`/public/ask/${token}/extract`).then((r) => {
      const p = r.data.proposed || {};
      setForm({ first_name: p.first_name || "", last_name: p.last_name || "", state: p.state || "",
        zip_code: p.zip_code || "", on_medicare: p.on_medicare, needs: p.needs || [], consent_to_contact: false });
    }).catch(() => setForm({ needs: [] })).finally(() => setLoading(false));
  }, [open, token]);

  const save = async () => {
    if (!form.first_name?.trim()) { toast.error("Please add at least a first name."); return; }
    setBusy(true);
    try {
      await api.post(`/public/ask/${token}/create-household`, {
        first_name: form.first_name, last_name: form.last_name || null, state: form.state || null,
        zip_code: form.zip_code || null, on_medicare: form.on_medicare ?? null, phone: form.phone || null, email: form.email || null,
        needs: form.needs || [], consent_to_contact: !!form.consent_to_contact, confirmed: true });
      try {
        const fn = (form.first_name || "").trim();
        const ln = (form.last_name || "").trim();
        const fullName = ln && !fn.toLowerCase().endsWith(ln.toLowerCase()) ? `${fn} ${ln}` : fn;
        sessionStorage.setItem("ask_prefill", JSON.stringify({ name: fullName, email: (form.email || "").trim() }));
      } catch { /* ignore */ }
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ask-save-dialog">
        <DialogHeader><DialogTitle>Save your conversation privately</DialogTitle></DialogHeader>
        {loading ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div> : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Here's what I understood from our chat. Please correct anything before I save it — I only save what you confirm.</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">First name</Label><Input value={form.first_name || ""} onChange={upd("first_name")} data-testid="ask-save-first" /></div>
              <div><Label className="text-xs">Last name</Label><Input value={form.last_name || ""} onChange={upd("last_name")} data-testid="ask-save-last" /></div>
              <div><Label className="text-xs">State</Label><Input value={form.state || ""} onChange={upd("state")} placeholder="FL" data-testid="ask-save-state" /></div>
              <div><Label className="text-xs">ZIP</Label><Input value={form.zip_code || ""} onChange={upd("zip_code")} data-testid="ask-save-zip" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={form.phone || ""} onChange={upd("phone")} inputMode="tel" placeholder="(555) 555-5555" data-testid="ask-save-phone" /></div>
              <div><Label className="text-xs">Email</Label><Input value={form.email || ""} onChange={upd("email")} inputMode="email" data-testid="ask-save-email" /></div>
            </div>
            <label className="flex items-start gap-2 text-sm pt-1">
              <Checkbox checked={!!form.consent_to_contact} onCheckedChange={(v) => setForm((f) => ({ ...f, consent_to_contact: !!v }))} data-testid="ask-save-consent" />
              <span>It’s okay for a licensed insurance agent to contact me about my Medicare options. (Optional — not required to save.)</span>
            </label>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy || loading} className="bg-[#0B192C] text-white" data-testid="ask-save-confirm">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save to My Nauticus"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AdvisorDialog({ open, onOpenChange, token, hasHousehold, onNeedSave, reason }) {
  const [form, setForm] = useState({ consent: false, shareTranscript: false, smsOptIn: false });
  const [busy, setBusy] = useState(false);
  const known = useContactPrefill(open && hasHousehold, token ? `/public/ask/${token}/contact-prefill` : null, setForm);
  const consentOnFile = !!known?.contact_consent?.on_file;
  const SHARE_TEXT = "I agree to share my full Ask Nauticus conversation with Senior Benefit Advisors FL so my licensed insurance agent has complete context.";
  const SMS_OPTIN_TEXT = "Senior Benefit Advisors may text me about my Medicare/insurance questions and appointments. Message frequency varies; msg & data rates may apply. Reply STOP to opt out, HELP for help.";
  const submit = async () => {
    if (!hasHousehold) { onNeedSave(); return; }
    if (!form.phone && !form.email) { toast.error("Add a phone or email."); return; }
    if (!form.consent && !consentOnFile) { toast.error("Please give consent to be contacted."); return; }
    setBusy(true);
    try {
      await api.post(`/public/ask/${token}/handoff`, {
        phone: form.phone || null, email: form.email || null, consent: !!form.consent,
        share_transcript: form.shareTranscript, share_transcript_text: SHARE_TEXT,
        sms_opt_in: !!form.smsOptIn, sms_opt_in_text: form.smsOptIn ? SMS_OPTIN_TEXT : null, request_reason: reason || null });
      toast.success("Done — a licensed insurance agent will follow up with your conversation in hand.");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ask-advisor-dialog">
        <DialogHeader><DialogTitle>Talk to a licensed insurance agent</DialogTitle></DialogHeader>
        {!hasHousehold ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">So a licensed insurance agent has your conversation for context, save to My Nauticus first. It only takes a moment.</p>
            <Button onClick={onNeedSave} className="bg-[#0B192C] text-white" data-testid="ask-advisor-need-save">Save & continue</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">A licensed insurance agent will reach out. A short summary of your chat is shared with them so you don't repeat yourself.</p>
            <div><Label className="text-xs">Phone</Label><Input value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="ask-advisor-phone" /></div>
            <div><Label className="text-xs">Email</Label><Input value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="ask-advisor-email" /></div>
            {consentOnFile ? <ConsentOnFile testid="ask-advisor-consent-on-file" /> : (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={!!form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} data-testid="ask-advisor-consent" />
              <span>A licensed insurance agent may contact me by phone, text, or email about my Medicare options.</span>
            </label>)}
            <label className="flex items-start gap-2 text-sm rounded-lg border border-[#E7E4DB] bg-[#F7F6F2] p-3">
              <Checkbox checked={!!form.shareTranscript} onCheckedChange={(v) => setForm({ ...form, shareTranscript: !!v })} data-testid="ask-advisor-share-transcript" />
              <span>{SHARE_TEXT} <span className="opacity-70">(Optional — without this, only a short summary is shared.)</span></span>
            </label>
            {known?.sms_opt_in_on_file ? <p className="text-sm text-[#33475B]" data-testid="ask-advisor-sms-on-file">Texting is already allowed for this number. Reply STOP anytime to opt out.</p> : (
            <label className="flex items-start gap-2 text-sm rounded-lg border border-[#E7E4DB] bg-[#F7F6F2] p-3">
              <Checkbox checked={!!form.smsOptIn} onCheckedChange={(v) => setForm({ ...form, smsOptIn: !!v })} data-testid="ask-advisor-sms-optin" />
              <span>{SMS_OPTIN_TEXT} <span className="opacity-70">(Optional — leave unchecked if you prefer not to receive texts.)</span></span>
            </label>)}
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-[#0B192C] text-white" data-testid="ask-advisor-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Request an agent"}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
